import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, GitBranch, SlidersHorizontal } from "lucide-react";
import { useSettingsProfiles } from "@/api/queries/settingsProfiles";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { relativeTime } from "@/lib/relativeTime";
import { CreateSettingsProfileDialog } from "@/components/settings-profile-actions/CreateSettingsProfileDialog";
import { SeedRepoDialog } from "@/components/gitea/SeedRepoDialog";
import { giteaSeedUrl } from "@/config/env";

const ROW_LIMIT = 50;

export function SettingsProfilesList() {
  const { data, isLoading, isError, error, refetch } = useSettingsProfiles();
  const navigate = useNavigate();
  const rows = (data ?? []).slice(0, ROW_LIMIT);
  const [createOpen, setCreateOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">{data?.length ?? 0} Config-Templates</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSeedOpen(true)} data-testid="action-seed-default-repo">
            <GitBranch className="h-4 w-4" />
            Template nach Gitea seeden
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="action-create-settings-profile">
            <Plus className="h-4 w-4" />
            Neues Profil
          </Button>
        </div>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && (data?.length ?? 0) === 0}
        emptyMessage="Noch keine Settings-Profile"
        emptyHint="Klick 'Neues Profil' zum Anlegen"
        onRetry={() => refetch()}
        rowLimit={ROW_LIMIT}
        totalRows={data?.length}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Beschreibung</TableHead>
              <TableHead>Seed-Repo</TableHead>
              <TableHead className="text-right">Erstellt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-clickable="true" onClick={() => navigate(`/settings-profiles/${p.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                      <SlidersHorizontal className="h-4 w-4" />
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-text">{p.name}</span>
                      {p.id === "standard" && <Badge variant="enrolled">Default</Badge>}
                    </div>
                  </div>
                </TableCell>
                <TableCell><span className="text-[13px] text-text2">{p.description || "—"}</span></TableCell>
                <TableCell>
                  {p.seedRepo ? (
                    <a
                      href={giteaSeedUrl(p.seedRepo, p.seedBranch)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mono text-[12px] text-accent-text hover:underline"
                    >
                      {p.seedRepo}{p.seedBranch ? `@${p.seedBranch}` : ""}
                    </a>
                  ) : (
                    <span className="text-text3">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right"><span className="text-[12.5px] text-text3">{relativeTime(p.createdAt)}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {createOpen && <CreateSettingsProfileDialog open onOpenChange={setCreateOpen} />}
      {seedOpen && <SeedRepoDialog open onOpenChange={setSeedOpen} />}
    </div>
  );
}
